import type { ArticleData } from "../types/ArticleData";

import {MouseEvent, ReactElement} from "react";

export type WikiArticleLinkTarget = Readonly<{
    id?: number;
    title?: string;
}>;

type Props = Readonly<{
    article: ArticleData;
    showCategories?: boolean;
    showAttributions?: boolean;
    onArticleLinkClick?: (target: WikiArticleLinkTarget) => void;
}>;

export default function WikiArticleDisplayPanel({article, showCategories = true, showAttributions = true, onArticleLinkClick}: Props): ReactElement {
    const handleContentClick = (event: MouseEvent<HTMLDivElement>): void => {
        if (!onArticleLinkClick) {
            return;
        }

        const anchor = (event.target as HTMLElement).closest("a");
        if (anchor == null || !event.currentTarget.contains(anchor)) {
            return;
        }

        const href = anchor.getAttribute("href");
        if (!href) {
            return;
        }

        let url: URL;
        try {
            url = new URL(href, window.location.origin);
        } catch {
            return;
        }

        const idParam = url.searchParams.get("id");
        const titleParam = url.searchParams.get("name") ?? url.searchParams.get("article");
        const id = idParam !== null && !isNaN(parseInt(idParam)) ? parseInt(idParam) : undefined;
        const title = titleParam ?? undefined;

        if (id === undefined && title === undefined) {
            return;
        }

        event.preventDefault();
        onArticleLinkClick({ id, title });
    };

    return (
        <>
            <h1 style={{ textAlign: "center" }}>{article.title}</h1>
            <hr />
            <div className="article-container">
                <div
                    className="article-content"
                    onClick={handleContentClick}
                    dangerouslySetInnerHTML={{
                        __html: article.text,
                    }}
                ></div>
                {showCategories && article.categories.length > 0 && (
                    <div
                        className="article-category-bar"
                        dangerouslySetInnerHTML={{
                            __html: `Categories: ${article.categories
                                .map(
                                    (category) =>
                                        `<a href="?category=${category}">${category}</a>`
                                )
                                .join(" | ")}`,
                        }}
                    ></div>
                )}
                {showAttributions && 
                    <div className="article-footer">
                        <div className="footer-left">
                            {article.creator == null
                                ? null
                                : `Creator: ${article.creator.name}`}
                        </div>
                        <div className="footer-right">
                            {article.editor == null
                                ? null
                                : `Last edit by: ${article.editor.name}`}
                        </div>
                    </div>
                }
            </div>
        </>
    );
}